import axios from "axios";
import { getAuthHeaders } from "../../../../utils/authUtils";
import { API_BASE_URL } from "../../../../environment";

export interface WorkflowUser {
  user_id: number;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
}

export interface WorkflowTenant {
  tenant_id: number;
  tenant_name: string;
}

export interface WorkflowCondition {
  field: string;
  value: string | string[];
  operator: string;
  join?: string;
}

export interface WorkflowConfigJson {
  filter_config: {
    conditions: WorkflowCondition[];
  };
  target_entity_config: {
    create: string[];
  };
}

export interface CrmWorkflow {
  crm_workflow_id: number;
  crm_workflow_display_name: string | null;
  crm_workflow_class_name: string | null;
  config_name: string;
  workflow_type: string;
  is_enabled: boolean;
  config_json: WorkflowConfigJson;
  created_datetime: string;
  updated_datetime: string;
  tenant_id: number;
  created_by: WorkflowUser;
  updated_by: WorkflowUser;
  tenant: WorkflowTenant;
}

export interface EntityField {
  field_name: string;
  display_name: string;
  data_type: string;
  allowed_operators: string[];
}

export interface EntityFieldResponse {
  entity_name: string;
  fields: EntityField[];
}

export interface RuleOperator {
  approval_rule_operator_id: number;
  approval_rule_operator_key: string;
  approval_rule_operator_display_name: string;
  operator: string;
  input_type: string;
  description: string;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface CreateWorkflowPayload {
  config_name: string;
  is_enabled: boolean;
  config_json: WorkflowConfigJson;
  tenant_id: number | string;
  created_by: number;
  updated_by: number;
}

export const AutoCreationRulesService = {
  fetchWorkflows: async (tenantId: string | number): Promise<CrmWorkflow[]> => {
    const response = await axios.get<PaginatedResponse<CrmWorkflow>>(
      `${API_BASE_URL}/crm-workflow/?tenant_id=${tenantId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchEntityFields: async (entityName: string): Promise<EntityField[]> => {
    const response = await axios.get<EntityFieldResponse>(
      `${API_BASE_URL}/lookup/entity-field/?entity_name=${entityName}`,
      { headers: getAuthHeaders() }
    );
    return response.data.fields || [];
  },

  fetchRuleOperators: async (): Promise<RuleOperator[]> => {
    const response = await axios.get<PaginatedResponse<RuleOperator>>(
      `${API_BASE_URL}/approval-rule-operator/`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  createWorkflow: async (payload: CreateWorkflowPayload): Promise<CrmWorkflow> => {
    const response = await axios.post<CrmWorkflow>(
      `${API_BASE_URL}/crm-workflow/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  updateWorkflow: async (id: number | string, payload: Partial<CreateWorkflowPayload>): Promise<CrmWorkflow> => {
    const response = await axios.patch<CrmWorkflow>(
      `${API_BASE_URL}/crm-workflow/${id}/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  deleteWorkflow: async (id: number | string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/crm-workflow/${id}/`, {
      headers: getAuthHeaders(),
    });
  },

  toggleWorkflow: async (id: number | string, isEnabled: boolean): Promise<CrmWorkflow> => {
    const response = await axios.patch<CrmWorkflow>(
      `${API_BASE_URL}/crm-workflow/${id}/`,
      { is_enabled: isEnabled },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
};
